import type { GapInboxItem, GapSolutionResponse } from "./types";
import { SectionCard } from "./SectionCard";
import { useState } from "react";
import { KbGapInboxItemCard } from "./gap-inbox/KbGapInboxItemCard";
import { KbGapInboxPagination } from "./gap-inbox/KbGapInboxPagination";
import type { KbGapInboxSectionProps } from "./gap-inbox/kbGapInboxTypes";
import { KbGapInboxToolbar } from "./gap-inbox/KbGapInboxToolbar";

export type { KbGapInboxSectionProps };

export function KbGapInboxSection(props: KbGapInboxSectionProps) {
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | null>(null);
  const [loadingSuggestionId, setLoadingSuggestionId] = useState<string | null>(null);
  const [suggestionByItemId, setSuggestionByItemId] = useState<Record<string, GapSolutionResponse | undefined>>({});
  const visibleItems = props.gapInboxItems;

  async function openSuggestion(item: GapInboxItem) {
    setActiveSuggestionId((current) => (current === item.id ? null : item.id));
    if (suggestionByItemId[item.id]) return;
    setLoadingSuggestionId(item.id);
    try {
      const result = await props.onSuggestGapSolution(item);
      if (!result) return;
      setSuggestionByItemId((prev) => ({ ...prev, [item.id]: result }));
    } finally {
      setLoadingSuggestionId(null);
    }
  }

  return (
    <SectionCard
      title="KB Gap Inbox"
      description="Persisted knowledge gaps and article link reviews surfaced by AI drafting sessions. Save items from AI processing above, then track them here."
      rightSlot={
        <div className="flex items-center gap-2">
          {props.gapInboxOpenCount > 0 ? (
            <span className="text-[11px] rounded-full border border-amber-400 bg-amber-100 px-2 py-1 text-amber-900 font-semibold animate-pulse dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100">
              {props.gapInboxOpenCount} open
            </span>
          ) : null}
        </div>
      }
    >
      <div className="space-y-3">
        <KbGapInboxToolbar {...props} />

        {visibleItems.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500 dark:border-zinc-600 dark:bg-zinc-900/40 dark:text-zinc-400">
            {props.gapInboxTestingOnly
              ? "No KB testing-mode items found for the current status filter."
              : "No inbox items loaded. Use Refresh to fetch persisted gaps and link reviews. Items are saved from AI drafting sessions using the Save to inbox button."}
          </div>
        ) : (
          <div className="space-y-3">
            {visibleItems.map((item) => (
              <KbGapInboxItemCard
                key={item.id}
                item={item}
                section={props}
                activeSuggestionId={activeSuggestionId}
                loadingSuggestionId={loadingSuggestionId}
                suggestionByItemId={suggestionByItemId}
                onToggleSuggestion={openSuggestion}
              />
            ))}
            <KbGapInboxPagination {...props} />
          </div>
        )}
      </div>
    </SectionCard>
  );
}
